import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LiveSessionProvider } from "@/contexts/LiveSessionContext";
import Layout from "@/components/Layout";
import AppHub from "@/pages/AppHub";
import AdminVideollamada from "@/pages/admin/AdminVideollamada";
import AdminColaInformes from "@/pages/admin/AdminColaInformes";
import CalendarioClases from "@/pages/CalendarioClases";
import Login from "@/pages/Login";
import RecoverAccess from "@/pages/RecoverAccess";
import ComingSoon from "@/pages/ComingSoon";
import ChangePassword from "@/pages/ChangePassword";

// Admin
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminFranquicias from "@/pages/admin/AdminFranquicias";
import AdminJerarquia from "@/pages/admin/AdminJerarquia";
import LiveClassRoom from "@/pages/LiveClassRoom";
import AdminAuditoria from "@/pages/admin/AdminAuditoria";
import AdminPerfilesPermisos from "@/pages/admin/AdminPerfilesPermisos";
import AdminPlanQA from "@/pages/admin/AdminPlanQA";
import AdminRepositorioSuper from "@/pages/admin/AdminRepositorioSuper";
import AdminReportes from "@/pages/admin/AdminReportes";
import AdminPerfilesEstudiantes from "@/pages/admin/AdminPerfilesEstudiantes";
import AdminCapacitacion from "@/pages/admin/AdminCapacitacion";
import DocenteCursos from "@/pages/docente/DocenteCursos";
import Comunicaciones from "@/pages/Comunicaciones";
import ContactoLanding from "@/pages/ContactoLanding";
import EstudiantePlanIA from "@/pages/estudiante/EstudiantePlanIA";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminPagos from "@/pages/admin/AdminPagos";
import AdminRoles from "@/pages/admin/AdminRoles";
import AdminSystemConfig from "@/pages/admin/AdminSystemConfig";
import AdminConfigMotor from "@/pages/admin/AdminConfigMotor";
import UserDetail from "@/pages/admin/UserDetail";
import Documentacion from "@/pages/Documentacion";

// Hito 2 — CEP / CAD
import AdminCuestionarios from "@/pages/admin/AdminCuestionarios";
import BibliotecaRecursos from "@/pages/BibliotecaRecursos";
import AdminCadEditor from "@/pages/admin/AdminCadEditor";
import DocenteCuestionarios from "@/pages/docente/DocenteCuestionarios";
import DocenteMisEstudiantes from "@/pages/docente/DocenteMisEstudiantes";
import DocenteResultadosAcademicos from "@/pages/docente/DocenteResultadosAcademicos";
import HistorialAcademico from "@/pages/docente/HistorialAcademico";
import {
  EstudianteCuestionarios,
  EstudianteCuestionarioPlay,
} from "@/pages/estudiante/EstudianteCuestionarios";
import EstudiantePerfil from "@/pages/estudiante/EstudiantePerfil";
import EstudianteRuta from "@/pages/estudiante/EstudianteRuta";
import EvaluacionClase from "@/pages/estudiante/EvaluacionClase";

// Franquiciado
import FranquiciadoFranquicia from "@/pages/franquiciado/FranquiciadoFranquicia";

// Hito 3 — Clases en vivo
import LiveClasses from "@/pages/LiveClasses";
import MonitorClases from "@/pages/MonitorClases";
import MisGrabaciones from "@/pages/MisGrabaciones";

// Dashboards ejecutivos + perfil corporativo
import AdminDashboard from "@/pages/admin/AdminDashboard";
import DocenteDashboard from "@/pages/docente/DocenteDashboard";
import RutaEnsenanzaView from "@/pages/RutaEnsenanzaView";
import EstudianteDetalle from "@/pages/docente/EstudianteDetalle";
import EvaluarEstudiante from "@/pages/docente/EvaluarEstudiante";
import EditarPlan from "@/pages/docente/EditarPlan";
import MiPerfil from "@/pages/MiPerfil";
import GuestJoin from "@/pages/GuestJoin";

const ROLE_HOME = {
  admin: "/admin/inicio",
  franquiciado: "/franquiciado/inicio",
  docente: "/docente/inicio",
  estudiante: "/estudiante/inicio",
};

const RequireRole = ({ roles, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6E7591] font-medium">Cargando…</div>;
  if (!user) return <Navigate to="/" replace />;
  // Si tiene must_change_password, fuerza al usuario a la pantalla de cambio.
  if (user.must_change_password && location.pathname !== "/cambiar-clave-inicial") {
    return <Navigate to="/cambiar-clave-inicial" replace />;
  }
  const allowed = Array.isArray(roles) ? roles : [roles];
  if (!allowed.includes(user.role)) {
    return <Navigate to={ROLE_HOME[user.role] || "/"} replace />;
  }
  return children;
};

const RequireModule = ({ module, children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Super admin NUNCA es bloqueado por módulos
  if (user?.role === "admin") return children;
  const modules = user?.effective_modules || [];
  if (!modules.includes(module)) {
    return <Navigate to={ROLE_HOME[user?.role] || "/"} replace />;
  }
  return children;
};

// Wrapper para /cambiar-clave-inicial — sólo accesible si está autenticado.
const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <LiveSessionProvider>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/contacto" element={<ContactoLanding />} />
            <Route path="/recuperar" element={<RecoverAccess />} />
            <Route path="/invitado/:classId" element={<GuestJoin />} />
            <Route path="/cambiar-clave-inicial" element={<RequireAuth><ChangePassword /></RequireAuth>} />

            {/* Admin */}
            <Route element={<RequireRole roles="admin"><Layout /></RequireRole>}>
              <Route path="/admin" element={<Navigate to="/admin/inicio" replace />} />
              <Route path="/admin/inicio" element={<AppHub />} />
              <Route path="/admin/calendario" element={<CalendarioClases />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/mi-perfil" element={<MiPerfil />} />
              <Route path="/admin/franquicias" element={<AdminFranquicias />} />
              <Route path="/admin/usuarios" element={<AdminUsers />} />
              <Route path="/admin/usuario/:id" element={<UserDetail />} />
              <Route path="/admin/perfiles-permisos" element={<AdminPerfilesPermisos />} />
              <Route path="/admin/roles" element={<AdminRoles />} />
              <Route path="/admin/cuestionarios" element={<AdminCuestionarios />} />
              <Route path="/admin/biblioteca-recursos" element={<BibliotecaRecursos />} />
              <Route path="/admin/cad-editor/:code" element={<AdminCadEditor />} />
              <Route path="/admin/resultados-cep" element={<DocenteCuestionarios mode="responses" otherTo="/admin/asignar-cuestionarios" otherLabel="Asignar diagnósticos" />} />
              <Route path="/admin/asignar-cuestionarios" element={<DocenteCuestionarios otherTo="/admin/resultados-cep" otherLabel="Ver resultados de diagnósticos" />} />
              <Route path="/admin/auditoria" element={<AdminAuditoria />} />
              <Route path="/admin/plan-qa" element={<RequireModule module="MQA"><AdminPlanQA /></RequireModule>} />
              <Route path="/admin/repositorio" element={<RequireModule module="MREPO"><AdminRepositorioSuper /></RequireModule>} />
              <Route path="/admin/reportes" element={<RequireModule module="M9"><AdminReportes /></RequireModule>} />
              <Route path="/admin/perfiles-estudiantes" element={<RequireModule module="M8"><AdminPerfilesEstudiantes /></RequireModule>} />
              <Route path="/admin/diagnosticos-inteligentes" element={<Navigate to="/admin/perfiles-estudiantes" replace />} />
              <Route path="/admin/estudiante/:id" element={<EstudianteDetalle />} />
              <Route path="/admin/estudiante/:id/evaluar" element={<EvaluarEstudiante />} />
              <Route path="/admin/estudiante/:id/plan/editar" element={<EditarPlan />} />
              <Route path="/admin/ruta-ensenanza/:reportId" element={<RutaEnsenanzaView />} />
              <Route path="/admin/capacitacion" element={<RequireModule module="M7"><AdminCapacitacion /></RequireModule>} />
              <Route path="/admin/comunicaciones" element={<RequireModule module="M11"><Comunicaciones /></RequireModule>} />
              <Route path="/admin/marketing" element={<RequireModule module="M13"><AdminMarketing /></RequireModule>} />
              <Route path="/admin/pagos" element={<RequireModule module="M12"><AdminPagos /></RequireModule>} />
              <Route path="/admin/configuracion" element={<AdminSystemConfig />} />
              <Route path="/admin/configuracion-motor" element={<AdminConfigMotor />} />
              <Route path="/admin/videollamada" element={<AdminVideollamada mode="estado" />} />
              <Route path="/admin/cola-informes" element={<AdminColaInformes />} />
              <Route path="/admin/videollamada/configuracion" element={<AdminVideollamada mode="configuracion" />} />
              <Route path="/admin/configuracion-ia" element={<Navigate to="/admin/configuracion-motor" replace />} />
              <Route path="/admin/clases-en-vivo" element={<LiveClasses />} />
              <Route path="/admin/monitor-clases" element={<MonitorClases />} />
              <Route path="/admin/grabaciones" element={<MisGrabaciones />} />
              <Route path="/admin/clases-en-vivo/:classId/sala" element={<LiveClassRoom />} />
              <Route path="/admin/jerarquia" element={<AdminJerarquia />} />
              <Route path="/admin/documentacion" element={<Documentacion />} />
              <Route path="/admin/finanzas" element={<ComingSoon title="Resumen financiero" />} />
              <Route path="/admin/*" element={<Navigate to="/admin/inicio" replace />} />
            </Route>

            {/* Franquiciado */}
            <Route element={<RequireRole roles="franquiciado"><Layout /></RequireRole>}>
              <Route path="/franquiciado" element={<Navigate to="/franquiciado/inicio" replace />} />
              <Route path="/franquiciado/inicio" element={<AppHub />} />
              <Route path="/franquiciado/calendario" element={<CalendarioClases />} />
              <Route path="/franquiciado/mi-perfil" element={<MiPerfil />} />
              <Route path="/franquiciado/dashboard" element={<Navigate to="/franquiciado/franquicia" replace />} />
              <Route path="/franquiciado/usuarios" element={<AdminUsers />} />
              <Route path="/franquiciado/usuario/:id" element={<UserDetail />} />
              <Route path="/franquiciado/perfiles-permisos" element={<AdminPerfilesPermisos />} />
              <Route path="/franquiciado/roles" element={<AdminRoles />} />
              <Route path="/franquiciado/franquicia" element={<FranquiciadoFranquicia />} />
              <Route path="/franquiciado/resultados-cep" element={<DocenteCuestionarios mode="responses" otherTo="/franquiciado/asignar-cuestionarios" otherLabel="Asignar diagnósticos" />} />
              <Route path="/franquiciado/asignar-cuestionarios" element={<DocenteCuestionarios otherTo="/franquiciado/resultados-cep" otherLabel="Ver resultados de diagnósticos" />} />
              <Route path="/franquiciado/auditoria" element={<AdminAuditoria />} />
              <Route path="/franquiciado/clases-en-vivo" element={<LiveClasses />} />
              <Route path="/franquiciado/grabaciones" element={<MisGrabaciones />} />
              <Route path="/franquiciado/clases-en-vivo/:classId/sala" element={<LiveClassRoom />} />
              <Route path="/franquiciado/jerarquia" element={<AdminJerarquia />} />
              <Route path="/franquiciado/reportes" element={<RequireModule module="M9"><AdminReportes /></RequireModule>} />
              <Route path="/franquiciado/perfiles-estudiantes" element={<RequireModule module="M8"><AdminPerfilesEstudiantes /></RequireModule>} />
              <Route path="/franquiciado/comunicaciones" element={<RequireModule module="M11"><Comunicaciones /></RequireModule>} />
              <Route path="/franquiciado/marketing" element={<RequireModule module="M13"><AdminMarketing /></RequireModule>} />
              <Route path="/franquiciado/pagos" element={<RequireModule module="M12"><AdminPagos /></RequireModule>} />
              <Route path="/franquiciado/finanzas" element={<ComingSoon title="Resumen financiero" />} />
              <Route path="/franquiciado/documentacion" element={<Documentacion />} />
              <Route path="/franquiciado/*" element={<Navigate to="/franquiciado/inicio" replace />} />
            </Route>

            {/* Docente */}
            <Route element={<RequireRole roles="docente"><Layout /></RequireRole>}>
              <Route path="/docente" element={<Navigate to="/docente/inicio" replace />} />
              <Route path="/docente/inicio" element={<AppHub />} />
              <Route path="/docente/dashboard" element={<DocenteDashboard />} />
              <Route path="/docente/mi-perfil" element={<MiPerfil />} />
              <Route path="/docente/mis-estudiantes" element={<DocenteMisEstudiantes />} />
              <Route path="/docente/perfiles-estudiantes" element={<RequireModule module="M8"><AdminPerfilesEstudiantes /></RequireModule>} />
              <Route path="/docente/diagnosticos-inteligentes" element={<Navigate to="/docente/perfiles-estudiantes" replace />} />
              <Route path="/docente/estudiante/:id" element={<EstudianteDetalle />} />
              <Route path="/docente/estudiantes/:id" element={<EstudianteDetalle />} />
              <Route path="/docente/estudiante/:id/evaluar" element={<EvaluarEstudiante />} />
              <Route path="/docente/estudiante/:id/plan/editar" element={<EditarPlan />} />
              <Route path="/docente/estudiantes/:id/evaluar" element={<EvaluarEstudiante />} />
              <Route path="/docente/estudiantes/:id/plan/editar" element={<EditarPlan />} />
              <Route path="/docente/ruta-ensenanza/:reportId" element={<RutaEnsenanzaView />} />
              <Route path="/docente/usuario/:id" element={<UserDetail />} />
              <Route path="/docente/resultados" element={<DocenteCuestionarios mode="responses" otherTo="/docente/cuestionarios" otherLabel="Asignar diagnósticos" />} />
              <Route path="/docente/cuestionarios" element={<DocenteCuestionarios otherTo="/docente/resultados" otherLabel="Ver resultados de diagnósticos" />} />
              <Route path="/docente/biblioteca-recursos" element={<BibliotecaRecursos />} />
              <Route path="/docente/resultados-academicos" element={<DocenteResultadosAcademicos />} />
              <Route path="/docente/estudiante/:id/evaluaciones" element={<HistorialAcademico />} />
              <Route path="/docente/calendario" element={<CalendarioClases />} />
              <Route path="/docente/clases-en-vivo" element={<LiveClasses />} />
              <Route path="/docente/clases-en-vivo/:classId/sala" element={<LiveClassRoom />} />
              <Route path="/docente/grabaciones" element={<MisGrabaciones />} />
              <Route path="/docente/cursos" element={<RequireModule module="M7"><DocenteCursos /></RequireModule>} />
              <Route path="/docente/comunicaciones" element={<RequireModule module="M11"><Comunicaciones /></RequireModule>} />
              <Route path="/docente/documentacion" element={<Documentacion />} />
              <Route path="/docente/*" element={<Navigate to="/docente/inicio" replace />} />
            </Route>

            {/* Estudiante */}
            <Route element={<RequireRole roles="estudiante"><Layout /></RequireRole>}>
              <Route path="/estudiante" element={<Navigate to="/estudiante/inicio" replace />} />
              <Route path="/estudiante/inicio" element={<AppHub />} />
              <Route path="/estudiante/calendario" element={<CalendarioClases />} />
              <Route path="/estudiante/perfil" element={<EstudiantePerfil />} />
              <Route path="/estudiante/ruta" element={<EstudianteRuta />} />
              <Route path="/estudiante/cuestionarios" element={<EstudianteCuestionarios />} />
              <Route path="/estudiante/cuestionario/:assignmentId" element={<EstudianteCuestionarioPlay />} />
              <Route path="/estudiante/clases-en-vivo" element={<LiveClasses />} />
              <Route path="/estudiante/clases-en-vivo/:classId/sala" element={<LiveClassRoom />} />
              <Route path="/estudiante/grabaciones" element={<MisGrabaciones />} />
              <Route path="/estudiante/evaluacion-clase" element={<EvaluacionClase />} />
              <Route path="/estudiante/comunicaciones" element={<RequireModule module="M11"><Comunicaciones /></RequireModule>} />
              <Route path="/estudiante/plan-ia" element={<RequireModule module="M4"><EstudiantePlanIA /></RequireModule>} />
              <Route path="/estudiante/cursos" element={<Navigate to="/estudiante/ruta" replace />} />
              <Route path="/estudiante/documentacion" element={<Documentacion />} />
              <Route path="/estudiante/*" element={<Navigate to="/estudiante/inicio" replace />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </LiveSessionProvider>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
